import React, { useEffect, useState } from 'react';
import AxlDialog from 'components/AxlDialog';
import { Box, Button, TextareaAutosize, Typography } from '@mui/material';
import AxlAutocomplete from 'components/AxlAutocomplete';
import _ from 'lodash';
import { getClientShipmentNumber, listAllClients } from '../../repositories/HttpRepository';
import Confirm from './Confirm';
import { numberFormatUtil } from 'utils/number';
import { toast } from 'react-toastify';

const uniqueOptions = (a, b) => a.label === b.label && a.value === b.value;
function AddNew({ isOpen, onClose, onConfirm, excludeClientIds }) {
  const [options, setOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [totalShipments, setTotalShipment] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [note, setNote] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);

  const clientOptions = _.uniqWith(
    options.map((option) => ({
      label: option.company,
      value: option.id,
      deleted: option._deleted,
    })),
    uniqueOptions,
  );

  // get all clients
  useEffect(() => {
    setIsLoading(true);

    listAllClients({ size: 1000000 }).then((res) => {
      setIsLoading(false);
      if (res.ok) {
        const enableClients = !!excludeClientIds ? res.data.filter((client) => !excludeClientIds.includes(client.id)) : res.data;
        setOptions(enableClients);
      }
    });
  }, []);

  // get total shipment number will be redacted
  useEffect(() => {
    if (selectedClient == null) return;

    setIsLoading(true);
    getClientShipmentNumber(selectedClient.value, {
      exclude_tags: 'REDACTED',
    }).then((res) => {
      setIsLoading(false);
      if (res.ok) {
        setTotalShipment(Number(res.data));
      } else {
        setTotalShipment(0);
        toast.error('Failed to get total shipment number.');
      }
    });
  }, [selectedClient]);

  // handle confirm
  const handleConfirm = () => {
    onConfirm(selectedClient.value, totalShipments, note);
  };

  const disableAddBtn = !selectedClient || isLoading || totalShipments === 0;

  return (
    <AxlDialog
      isOpen={isOpen}
      childrenTitle={'CREATE CUSTOMER DATA REDACTION REQUEST'}
      handleClose={onClose}
      childrenAction={
        <Box sx={{ display: 'flex' }} style={{ gap: 8 }}>
          <Button variant="outlined" color="secondary" onClick={() => onClose()} size="small">
            Cancel
          </Button>

          <Button disabled={disableAddBtn} onClick={() => setOpenConfirm(true)} disableElevation variant="contained" color="success" size="small">
            Add
          </Button>
        </Box>
      }
    >
      <Box sx={{ justifyContent: 'center', pb: 2 }}>
        <Box>
          <AxlAutocomplete
            style={{ width: '100%' }}
            options={clientOptions}
            label="Select client"
            getOptionLabel={(option) => `[${option.value}] ${option.label}${option.deleted ? ' [Deleted]' : ''}`}
            disabled={isLoading}
            value={selectedClient}
            handleChange={(_, selected) => {
              setSelectedClient(selected);
            }}
          />

          {selectedClient && (
            <Typography sx={{ fontSize: 13, mt: '5px', color: !isLoading ? 'warning.main' : undefined }}>
              {isLoading ? 'Counting shipments ...' : `${numberFormatUtil(totalShipments)} shipments will be affected.`}
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 2, mr: '22px' }}>
          <TextareaAutosize
            value={note}
            onChange={(e) => setNote(e.target.value)}
            variant="outlined"
            id="note"
            placeholder={'Add some notes here...'}
            minRows={5}
            disabled={isLoading}
            style={{ width: '100%', padding: '10px' }}
            maxLength={2048}
          />
        </Box>
      </Box>
      {openConfirm && <Confirm isOpen={openConfirm} clientId={selectedClient.value} clientName={selectedClient.label} onClose={() => setOpenConfirm(false)} onConfirm={handleConfirm} />}
    </AxlDialog>
  );
}

export default AddNew;
